'use client';
import Image, { ImageProps } from 'next/image';
import { shimmer, toBase64 } from '@/lib/utils/shimmer';
import { useEffect, useState, useMemo } from 'react';

interface ShimmerImageProps
  extends Omit<ImageProps, 'placeholder' | 'blurDataURL'> {
  fallbackSrc?: string;
  objectType?: 'company' | 'user';
  objectID?: number;
  alt: string;
  isResponsive?: boolean;
  isMedium?: boolean;
  gender?: number;
}

const IMAGES_PER_FOLDER = 2000;
const BASE_URL = process.env.NEXT_PUBLIC_CDN_URL;
// Ensure we only use a valid absolute base; otherwise fall back to same-origin relative paths
const SAFE_BASE_URL =
  typeof BASE_URL === 'string' && /^https?:\/\//.test(BASE_URL)
    ? BASE_URL.replace(/\/$/, '')
    : '';

const DEFAULT_FALLBACK_COMPANY_IMAGE = '/images/company.svg';

const ShimmerImage = ({
  width = 0,
  height = 0,
  src,
  objectType = 'company',
  objectID,
  alt,
  isResponsive = false,
  style,
  isMedium = false,
  gender = 0,
  ...props
}: ShimmerImageProps) => {
  const blurUrl = useMemo(
    () => `data:image/svg+xml;base64,${toBase64(shimmer(width, height))}`,
    [width, height]
  );

  const defaultAvatar = (gender?: number) => {
    if (gender === 1) return `${SAFE_BASE_URL}/r/blank_female.svg`;
    if (gender === 2) return `${SAFE_BASE_URL}/r/blank_male.svg`;
    return `${SAFE_BASE_URL}/r/blank_unknown.svg`;
  };

  const DEFAULT_FALLBACK_IMAGE = defaultAvatar(gender || 0);

  const [imgSrc, setImgSrc] = useState<string>(
    objectType === 'company' &&
      objectID != null &&
      typeof src === 'string' &&
      src.length > 0
      ? `${SAFE_BASE_URL}/logo/${Math.ceil(objectID / IMAGES_PER_FOLDER)}/${src}.png`
      : DEFAULT_FALLBACK_COMPANY_IMAGE
  );

  useEffect(() => {
    if (!objectID || typeof src !== 'string' || src.length === 0) return;

    if (objectType === 'company') {
      setImgSrc(
        `${SAFE_BASE_URL}/logo/${Math.ceil(objectID / IMAGES_PER_FOLDER)}/${src}.png`
      );
    } else if (objectType === 'user') {
      setImgSrc(
        `${SAFE_BASE_URL}${isMedium ? '/r/t/' : '/r/s/'}${Math.floor(objectID / 1000)}/${src}.jpg`
      );
    }
  }, [objectType, objectID, src, isMedium]);

  // Priority only for hero / above-the-fold images
  const isPriority = useMemo(
    () => objectType === 'company' && objectID != null && props.priority,
    [objectType, objectID, props.priority]
  );

  const handleError = () => {
    setImgSrc(
      objectType === 'company'
        ? DEFAULT_FALLBACK_COMPANY_IMAGE
        : DEFAULT_FALLBACK_IMAGE
    );
  };

  return (
    <Image
      src={imgSrc}
      alt={alt}
      style={{ ...style }}
      width={width}
      height={height}
      placeholder="blur"
      blurDataURL={blurUrl}
      priority={isPriority}
      loading={isPriority ? 'eager' : 'lazy'}
      fetchPriority={isPriority ? 'high' : 'low'}
      sizes={
        isResponsive
          ? '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
          : undefined
      }
      {...props}
      onError={handleError}
    />
  );
};

export default ShimmerImage;
